import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import type { Lane } from '../../types/domain';
import type { ResultAttempt } from '../../types/results';
import { formatTime } from '../../utils/timeFormat';
import type { TimeFormat } from '../../types/domain';
import { computeLaneOfficialTimeMs } from '../../utils/laneOfficialTime';

const LANE_LABELS: Record<Lane, string> = {
  LP: 'LP',
  PP: 'PP',
  TRACK_1: 'Dráha 1',
  TRACK_2: 'Dráha 2',
};

const LANE_TOOLTIPS: Record<Lane, string> = {
  LP: 'Levý proud',
  PP: 'Pravý proud',
  TRACK_1: 'Dráha 1',
  TRACK_2: 'Dráha 2',
};

interface Props {
  attempts: ResultAttempt[];
  showAttemptDetails: boolean;
  showLaneDetails: boolean;
  compact?: boolean;
  /** V kompaktni tabulce nechceme tooltipy — zbytecne prekryvaji tabulku. */
  disableTooltip?: boolean;
  /** Format casu z disciplíny — pokud není předán, fallback SS_CC (sekundy, konvence Plamen;
   *  shodne s ostatnimi rankingy). Vsichni soucasni volajici predavaji discipline.timeFormat. */
  timeFormat?: TimeFormat;
}

export const AttemptsDetail = ({
  attempts,
  showAttemptDetails,
  showLaneDetails,
  compact = false,
  disableTooltip = false,
  timeFormat = 'SS_CC',
}: Props) => {
  if (!showAttemptDetails && !showLaneDetails) return null;
  if (!attempts || attempts.length === 0) return null;

  if (showAttemptDetails) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: compact ? 0.25 : 0.5 }}
      >
        {attempts.map((a, idx) => {
          const laneLabel = LANE_LABELS[a.lane];
          const prefix = showLaneDetails ? `${laneLabel}${attempts.filter((x) => x.lane === a.lane).length > 1 ? `·${a.timerSlot}` : ''}` : `${idx + 1}.`;
          const value = a.isNp ? 'NP' : a.timeMs != null ? formatTime(a.timeMs, timeFormat) : '—';
          const chip = (
            <Chip
              size="small"
              variant="outlined"
              label={
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                  <strong>{prefix}</strong>&nbsp;{value}
                </Typography>
              }
              color={a.isNp ? 'error' : a.isElectronic ? 'primary' : 'default'}
              sx={{ height: 22 }}
            />
          );
          if (disableTooltip) return <span key={`${a.lane}-${a.timerSlot}-${idx}`}>{chip}</span>;
          return (
            <Tooltip
              key={`${a.lane}-${a.timerSlot}-${idx}`}
              title={`${LANE_TOOLTIPS[a.lane]} · Časoměřič ${a.timerSlot}`}
              placement="top"
              arrow
            >
              {chip}
            </Tooltip>
          );
        })}
      </Stack>
    );
  }

  // Jen showLaneDetails=true: zobraz oficialni cas kazde drahy jako jeden chip.
  const uniqueLanes = Array.from(new Set(attempts.map((a) => a.lane)));
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: compact ? 0.25 : 0.5 }}>
      {uniqueLanes.map((lane) => {
        const laneAttempts = attempts.filter((a) => a.lane === lane);
        const laneTime = computeLaneOfficialTimeMs(laneAttempts);
        const value = laneTime != null ? formatTime(laneTime, timeFormat) : '—';
        const chip = (
          <Chip
            size="small"
            variant="outlined"
            label={
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                <strong>{LANE_LABELS[lane]}</strong>&nbsp;{value}
              </Typography>
            }
            sx={{ height: 22 }}
          />
        );
        if (disableTooltip) return <span key={lane}>{chip}</span>;
        return (
          <Tooltip key={lane} title={LANE_TOOLTIPS[lane]} placement="top" arrow>
            {chip}
          </Tooltip>
        );
      })}
    </Stack>
  );
};
